// enerqiq-web/src/pages/PaymentAllocationsAdmin.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import {
  Box, Tabs, Tab, Paper, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Pagination, TextField, Button, Stack, MenuItem
} from '@mui/material'
import { withBase } from '../config/api'

const fmtMoney = (n) => (Number(n || 0)).toLocaleString('sk-SK', { style: 'currency', currency: 'EUR' })
const fmtDate = (d) => new Date(d).toLocaleString('sk-SK')

const CURRENCIES = ['EUR', 'USD', 'GBP']
const TYPES = ['CASHBACK_FUND', 'REFERRAL_FUND', 'MARGIN_FUND']

export default function PaymentAllocationsAdmin() {
  const [tab, setTab] = useState('cashback')
  const [balances, setBalances] = useState({ cashback: 0, referral: 0, margin: 0 })
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)

  const [filters, setFilters] = useState({
    from: '',
    to: '',
    search: '',
    paymentId: '',
    userId: '',
    plan: '',
    currency: '',
    type: ''
  })

  const [page, setPage] = useState(1)
  const limit = 25

  const [sortKey, setSortKey] = useState('date')
  const [sortDir, setSortDir] = useState('desc')

  useEffect(() => {
    axios
      .get(withBase('/admin/payment-allocations/summary'), { withCredentials: true })
      .then(res => setBalances(res.data?.balances || { cashback: 0, referral: 0, margin: 0 }))
      .catch(() => {})
  }, [])

  const fetchData = useCallback(() => {
    const offset = (page - 1) * limit
    axios
      .get(withBase('/admin/payment-allocations/movements'), {
        withCredentials: true,
        params: { account: tab, limit, offset, ...filters }
      })
      .then(res => {
        setItems(res.data?.items || [])
        setTotal(res.data?.total || 0)
      })
      .catch(() => {})
  }, [filters, page, tab])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  function applyFilters() {
    setPage(1)
    fetchData()
  }

  function toggleSort(key) {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  function indicator(key) {
    if (sortKey !== key) return ''
    return sortDir === 'asc' ? ' ↑' : ' ↓'
  }

  const sortedItems = useMemo(() => {
    const arr = [...items]
    const get = (row, key) => {
      const allocationType = row.allocationType ?? row.allocation_type ?? ''
      switch (key) {
        case 'date': return new Date(row.date).getTime()
        case 'paymentId': return Number(row.paymentId) || 0
        case 'userId': return Number(row.userId) || 0
        case 'plan': return String(row.usedPlan || '').toLowerCase()
        case 'amount': return Number(row.amount) || 0
        case 'currency': return String(row.currency || '').toLowerCase()
        case 'type': return String(allocationType).toLowerCase()
        default: return ''
      }
    }
    arr.sort((a, b) => {
      const va = get(a, sortKey)
      const vb = get(b, sortKey)
      if (va < vb) return sortDir === 'asc' ? -1 : 1
      if (va > vb) return sortDir === 'asc' ? 1 : -1
      return 0
    })
    return arr
  }, [items, sortKey, sortDir])

return (
  <Box
    sx={{
      background: '#ededed',
      padding: 1.5,
      border: '1.5px solid #107c6b',
      borderRadius: '10px',
      width: '100%',
      boxSizing: 'border-box',
      position: 'relative',
      height: '100%',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'auto'
    }}
  >
      <Typography variant="h5" sx={{ mb: 2 }}>Účtovné alokácie členstiev</Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => { setTab(v); setPage(1) }}>
          <Tab value="cashback" label="Cashback fond" />
          <Tab value="referral" label="Referral systém" />
          <Tab value="margin" label="Marža" />
        </Tabs>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body1">
            Zostatok:{' '}
            <b>
              {tab === 'cashback' && fmtMoney(balances.cashback)}
              {tab === 'referral' && fmtMoney(balances.referral)}
              {tab === 'margin' && fmtMoney(balances.margin)}
            </b>
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TextField label="Od dátumu" type="date" value={filters.from} onChange={(e) => setFilters(f => ({ ...f, from: e.target.value }))} InputLabelProps={{ shrink: true }} />
          <TextField label="Do dátumu" type="date" value={filters.to} onChange={(e) => setFilters(f => ({ ...f, to: e.target.value }))} InputLabelProps={{ shrink: true }} />
          <TextField label="Platba ID" value={filters.paymentId} onChange={(e) => setFilters(f => ({ ...f, paymentId: e.target.value }))} />
          <TextField label="Používateľ ID" value={filters.userId} onChange={(e) => setFilters(f => ({ ...f, userId: e.target.value }))} />
          <TextField label="Členstvo" value={filters.plan} onChange={(e) => setFilters(f => ({ ...f, plan: e.target.value }))} />
          <TextField select label="Mena" value={filters.currency} onChange={(e) => setFilters(f => ({ ...f, currency: e.target.value }))}>
            <MenuItem value="">(všetko)</MenuItem>
            {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField select label="Typ transakcie" value={filters.type} onChange={(e) => setFilters(f => ({ ...f, type: e.target.value }))}>
            <MenuItem value="">(všetko)</MenuItem>
            {TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField label="Hľadať" value={filters.search} onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))} sx={{ minWidth: 220 }} />
          <Button variant="contained" onClick={applyFilters}>Filtrovať</Button>
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['date', 'paymentId', 'userId', 'plan', 'amount', 'currency', 'type'].map((col) => (
                <TableCell key={col} onClick={() => toggleSort(col)} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  {col}{indicator(col)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedItems.length === 0 && (
              <TableRow><TableCell colSpan={7}>Žiadne pohyby</TableCell></TableRow>
            )}
            {sortedItems.map((row) => {
              const allocationType = row.allocationType ?? row.allocation_type ?? ''
              return (
                <TableRow key={row.id}>
                  <TableCell>{fmtDate(row.date)}</TableCell>
                  <TableCell>#{row.paymentId}</TableCell>
                  <TableCell>{row.userId}</TableCell>
                  <TableCell>{row.usedPlan}</TableCell>
                  <TableCell align="right">{fmtMoney(row.amount)}</TableCell>
                  <TableCell align="right">{row.currency || 'EUR'}</TableCell>
                  <TableCell>{allocationType}</TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Pagination
          count={Math.max(1, Math.ceil(total / limit))}
          page={page}
          onChange={(_, p) => setPage(p)}
          shape="rounded"
        />
      </Box>
    </Box>
  )
}
